import { Component, useEffect, useRef, useState, type ErrorInfo, type ReactNode } from 'react';

/**
 * A React error boundary — the one thing a function component cannot be.
 *
 * Pre-P8 punch list, Tier 2 (error handling): before this existed, ONE
 * uncaught render error on any screen unmounted the entire React tree —
 * React's own documented behaviour without a boundary — leaving a blank
 * white page with no message and no way back short of knowing to reload.
 * Two instances are mounted:
 *
 *   - GLOBAL (main.tsx), around <App/>: the last line of defence. Its
 *     fallback offers "Try again" (remounts App from scratch), "Reload", and
 *     "Sign out" — the last because App routes EVERY persisted session
 *     straight back into the manager portal, so a crash that is
 *     deterministic for a signed-in manager's data would otherwise re-enter
 *     itself on both of the first two, with no route to any working screen.
 *   - PER-VIEW (Dashboard.tsx), around whichever view is showing: a crash
 *     in, say, the Admin Console leaves AppShell's own sidebar/tab-bar and
 *     the environment banner standing, so the manager can just navigate
 *     somewhere else. `resetKey={view}` clears the caught error the
 *     moment the hash changes to a different view — so navigating away IS
 *     the recovery, no extra click needed. `onReset` is Dashboard's own
 *     refresh(), so "Try again" here means what the Dashboard's other "Try
 *     again" button already means: re-read the data, then re-render.
 *
 * What it deliberately does NOT do: report anywhere. There is no error
 * service in this project and no plan for one (MIGRATION_SCOPE §2.1's
 * "static SPA, no server"). React 19 already logs every caught error with
 * its component stack (the root's default onCaughtError); componentDidCatch
 * below logs once more so the line survives if that default is ever
 * replaced. The fallback shows the message in a collapsed "Technical
 * details" so a manager can read it out to whoever maintains this.
 *
 * Boundaries catch RENDER-phase errors and synchronous throws inside
 * effects — render, lifecycle methods, useEffect/useLayoutEffect bodies, and
 * the constructors of everything below them. They do NOT catch errors thrown
 * inside event handlers or from rejected promises — those already have
 * their own paths in this codebase (every write returns an ActionResult;
 * every load() has a rejection handler that setError()s).
 *
 * Adversarial-review fixes folded in before this ever shipped: role="alert"
 * sits on the message, not the <main> (ARIA in HTML forbids any role on
 * <main>, and the wrapper role was swallowing the page's only landmark); the
 * fallback takes focus when it appears, so keyboard and screen-reader users
 * land on the message instead of <body>; a SECOND crash after "Try again"
 * looks and reads differently ("Still not working", Reload promoted) rather
 * than repainting an identical card; "Back to Dashboard" only renders
 * when following it can actually change the view; and the reset-on-key-
 * change only fires for a key that changed AFTER the error was showing, not
 * on the same render that caught it (which used to double-throw).
 */

interface Props {
  children: ReactNode;
  /**
   * 'portal' → the fallback fills the viewport (nothing else is on screen).
   * 'view'   → it sits inline where the crashed view would have rendered,
   *            under the Dashboard's own header.
   */
  scope: 'portal' | 'view';
  /**
   * Any value whose change should clear a caught error and re-render the
   * children — the current view name, for the per-view boundary. Optional:
   * the global boundary has nothing to key on.
   */
  resetKey?: string;
  /**
   * Called on "Try again" just before the children re-render. The per-view
   * boundary passes Dashboard's refresh() so a retry also refetches the data
   * the crashed view rendered from — otherwise a data-shaped crash simply
   * re-throws on identical input.
   */
  onReset?: () => void;
  /**
   * View scope: when set, the fallback offers a "Back to Dashboard" link to
   * this href. Dashboard passes it for every OTHER view — never for its own,
   * since dashboard IS the landing view now (Manager Home retired,
   * redesign follow-up 2026-09-04) and a link back to itself could not
   * change resetKey; it would be a control that visibly does nothing.
   */
  homeHref?: string;
  /**
   * Portal scope: when set, the fallback offers "Sign out", which awaits this
   * and then remounts the app (now signed out, so it lands on the employee
   * request form rather than back in the crashed tree).
   */
  onSignOut?: () => Promise<unknown>;
}

interface State {
  error: Error | null;
  /** How many "Try again" presses have already ended back here. Reset when a retry succeeds or the key changes. */
  retries: number;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, retries: 0 };

  static getDerivedStateFromError(thrown: unknown): Pick<State, 'error'> {
    return { error: toError(thrown) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack ?? '');
  }

  override componentDidUpdate(prev: Readonly<Props>, prevState: Readonly<State>): void {
    // Navigation away. `prevState.error !== null` is essential: without it a
    // crash on the very render that changed the key (navigating straight
    // INTO a crashing view) would see prev.resetKey !== resetKey, reset at
    // once, re-render, re-crash and log twice before settling.
    if (prevState.error !== null && this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, retries: 0 });
      return;
    }
    // A retry that rendered the children without throwing has succeeded, so
    // the next crash — if any — is a fresh one, not "still failing".
    if (prevState.error !== null && this.state.error === null && this.state.retries > 0) {
      this.setState({ retries: 0 });
    }
  }

  private readonly retry = (): void => {
    this.props.onReset?.();
    this.setState((s) => ({ error: null, retries: s.retries + 1 }));
  };

  override render(): ReactNode {
    const { error, retries } = this.state;
    if (error === null) return this.props.children;
    return (
      <CrashFallback
        error={error}
        scope={this.props.scope}
        retries={retries}
        onRetry={this.retry}
        homeHref={this.props.homeHref}
        onSignOut={this.props.onSignOut}
      />
    );
  }
}

/** React hands the boundary whatever was thrown; make it an Error with a readable message. */
function toError(thrown: unknown): Error {
  if (thrown instanceof Error) return thrown;
  if (typeof thrown === 'string') return new Error(thrown);
  try {
    return new Error(JSON.stringify(thrown) ?? String(thrown));
  } catch {
    return new Error(String(thrown));
  }
}

function CrashFallback({
  error,
  scope,
  retries,
  onRetry,
  homeHref,
  onSignOut,
}: {
  error: Error;
  scope: Props['scope'];
  retries: number;
  onRetry: () => void;
  homeHref?: string;
  onSignOut?: () => Promise<unknown>;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [signingOut, setSigningOut] = useState(false);

  // The fallback is a fresh mount on every crash — including the crash right
  // after a failed "Try again" — so this puts focus (and a screen reader's
  // reading cursor) on the card each time, instead of leaving it on <body>.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const stillFailing = retries > 0;
  const Wrapper = scope === 'portal' ? 'main' : 'div';
  // The portal fallback IS the page, so it gets the page's h1; a view
  // fallback sits under whichever view's own h1 it replaced (every
  // AppShell-wrapped view has its own now — redesign Pass 2) and takes an
  // h2 like every section. Note this fallback mounts INSTEAD OF the crashed
  // view's own content, including that view's h1 — so a view-scope crash is
  // still an h2-only page, same as it always was; only the portal scope
  // guarantees an h1 is present.
  const Heading = scope === 'portal' ? 'h1' : 'h2';

  async function signOut() {
    if (!onSignOut) return;
    setSigningOut(true);
    try {
      await onSignOut();
    } finally {
      // Signed out (or the sign-out itself failed — either way the only
      // useful next step is a fresh mount, which App resolves from storage).
      onRetry();
    }
  }

  return (
    <Wrapper style={scope === 'portal' ? S.fullPage : S.inline}>
      <div ref={panelRef} tabIndex={-1} style={S.panel}>
        {/* role="alert" on the MESSAGE only — announced immediately, and the
            controls below stay out of the live region so they are not read
            as one run-on utterance (and toggling "Technical details" does
            not re-announce the whole card). */}
        <div role="alert">
          <Heading style={S.h}>{stillFailing ? 'Still not working' : 'Something went wrong'}</Heading>
          <p style={S.p}>
            {stillFailing
              ? 'Trying again did not fix it. Reloading the page usually does' +
                (homeHref ? ', or go back to the Dashboard.' : '.')
              : `${scope === 'portal' ? 'The portal' : 'This part of the portal'} hit an error it ` +
                'could not recover from on its own. Anything you had already submitted or saved is ' +
                'not affected — but anything you were still typing here will need to be entered ' +
                'again. If you were in the middle of sending something, check before sending it twice.'}
          </p>
        </div>
        <div style={S.actions}>
          {stillFailing ? (
            <>
              <button type="button" onClick={() => window.location.reload()} style={S.primary}>
                Reload the page
              </button>
              <button type="button" onClick={onRetry} style={S.secondary}>
                Try again
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onRetry} style={S.primary}>
                Try again
              </button>
              <button type="button" onClick={() => window.location.reload()} style={S.secondary}>
                Reload the page
              </button>
            </>
          )}
          {homeHref && (
            <a href={homeHref} style={S.link}>
              Back to Dashboard
            </a>
          )}
          {scope === 'portal' && onSignOut && (
            <button type="button" onClick={() => void signOut()} disabled={signingOut} style={S.link}>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          )}
        </div>
        <details style={S.details}>
          <summary style={S.summary}>Technical details</summary>
          <code style={S.code}>
            {error.name}: {error.message}
          </code>
        </details>
      </div>
    </Wrapper>
  );
}

// Same visual conventions as LoginScreen / Dashboard's own error panel:
// system-ui, white card on the portal's cool-grey field, brand blue action.
const S = {
  fullPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    background: '#F3F5F8',
    fontFamily: 'system-ui, sans-serif',
    color: '#1a1a1a',
  },
  inline: {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem 0',
    fontFamily: 'system-ui, sans-serif',
    color: '#1a1a1a',
  },
  panel: {
    width: '100%',
    maxWidth: '28rem',
    border: '1px solid #e3e7ee',
    borderRadius: '0.75rem',
    padding: '1.75rem 2rem',
    background: 'white',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    boxSizing: 'border-box',
  },
  h: { margin: '0 0 0.6rem', fontSize: '1.15rem' },
  p: { margin: '0 0 1.1rem', fontSize: '0.92rem', lineHeight: 1.55, color: '#444' },
  actions: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem' },
  // 44px minimum height on every control — this screen is reached in a bad
  // moment, often on a phone; the recovery controls must be easy to hit.
  primary: {
    minHeight: '2.75rem',
    padding: '0.6rem 1.1rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'white',
    background: '#0b46c4',
    border: 0,
    borderRadius: '0.4rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  secondary: {
    minHeight: '2.75rem',
    padding: '0.6rem 1.1rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#1a1a1a',
    background: 'white',
    border: '1px solid #c7d0dd',
    borderRadius: '0.4rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '2.75rem',
    padding: '0 0.4rem',
    fontSize: '0.92rem',
    fontWeight: 600,
    color: '#0b46c4',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textDecoration: 'underline',
  },
  details: { marginTop: '0.75rem', fontSize: '0.82rem', color: '#555' },
  // Vertical padding (not display:flex, which would drop the native
  // disclosure triangle) lifts the <summary> to a ~44px target like its
  // neighbours.
  summary: { cursor: 'pointer', padding: '0.85rem 0' },
  code: {
    display: 'block',
    marginTop: '0.25rem',
    padding: '0.5rem 0.65rem',
    borderRadius: '0.35rem',
    background: '#f4f5f7',
    fontSize: '0.78rem',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
} satisfies Record<string, React.CSSProperties>;
